/**
 * Page Permission Utilities
 * 
 * Provides helper functions to check user permissions on pages
 * Matches Notion's permission model:
 * - Viewer: Can only view content (read-only)
 * - Commenter: Can view and add comments
 * - Editor: Can edit content, title, icon
 * - Owner: Full control (can manage members, delete, change settings)
 */

// Role hierarchy for permission checks
const ROLE_HIERARCHY = {
  viewer: 1,
  commenter: 2,
  editor: 3,
  owner: 4
};

/**
 * Check if user has at least the required role
 * @param {string} userRole - User's current role
 * @param {string} requiredRole - Minimum required role
 * @returns {boolean}
 */
export const hasRequiredRole = (userRole, requiredRole) => {
  if (!userRole || !requiredRole) return false;
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
};

/**
 * Check if user can view the page
 * @param {string} userRole - User's role on the page
 * @returns {boolean}
 */
export const canView = (userRole) => {
  return hasRequiredRole(userRole, 'viewer');
};

/**
 * Check if user can add comments
 * @param {string} userRole - User's role on the page
 * @returns {boolean}
 */
export const canComment = (userRole) => {
  return hasRequiredRole(userRole, 'commenter');
};

/**
 * Check if user can edit page content, title, and icon
 * @param {string} userRole - User's role on the page
 * @returns {boolean}
 */
export const canEdit = (userRole) => {
  return hasRequiredRole(userRole, 'editor');
};

/**
 * Check if user can manage members (add/remove/change roles)
 * Only owners can manage members
 * @param {string} userRole - User's role on the page
 * @returns {boolean}
 */
export const canManageMembers = (userRole) => {
  return userRole === 'owner';
};

/**
 * Check if user can change page settings (visibility, etc.)
 * Only owners can change settings
 * @param {string} userRole - User's role on the page
 * @returns {boolean}
 */
export const canChangeSettings = (userRole) => {
  return userRole === 'owner';
};

/**
 * Check if user can delete or archive the page
 * Only owners can delete/archive
 * @param {string} userRole - User's role on the page
 * @returns {boolean}
 */
export const canDelete = (userRole) => {
  return userRole === 'owner';
};

/**
 * Check if user can move the page
 * Editors and owners can move pages
 * @param {string} userRole - User's role on the page
 * @returns {boolean}
 */
export const canMove = (userRole) => {
  return hasRequiredRole(userRole, 'editor');
};

/**
 * Check if user can duplicate the page
 * Editors and owners can duplicate pages
 * @param {string} userRole - User's role on the page
 * @returns {boolean}
 */
export const canDuplicate = (userRole) => {
  return hasRequiredRole(userRole, 'editor');
};

/**
 * Check if user can favorite/unfavorite the page
 * All users with view access can favorite
 * @param {string} userRole - User's role on the page
 * @returns {boolean}
 */
export const canFavorite = (userRole) => {
  return hasRequiredRole(userRole, 'viewer');
};

/**
 * Get permission level display name
 * @param {string} role - User's role
 * @returns {string}
 */
export const getRoleDisplayName = (role) => {
  const displayNames = {
    owner: 'Full access',
    editor: 'Can edit',
    commenter: 'Can comment',
    viewer: 'Can view'
  };
  return displayNames[role] || 'No access';
};

/**
 * Get permission level description
 * @param {string} role - User's role
 * @returns {string}
 */
export const getRoleDescription = (role) => {
  const descriptions = {
    owner: 'Edit, suggest, comment, and share with others',
    editor: 'Edit, suggest, and comment',
    commenter: 'Suggest and comment',
    viewer: 'View only'
  };
  return descriptions[role] || '';
};

